import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexto/AuthContexto';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Clock, Car, Droplets, LogOut, RefreshCw } from 'lucide-react';

const ESTADOS = [
  { key: 'en_espera', label: 'Pendiente', color: 'bg-amber-500/20 text-amber-400 border-amber-700' },
  { key: 'lavando', label: 'En Proceso', color: 'bg-sky-500/20 text-sky-400 border-sky-700' },
  { key: 'listo', label: 'Terminado', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-700' },
  { key: 'entregado', label: 'Entregado', color: 'bg-slate-500/20 text-slate-400 border-slate-600' },
];

const estadoColor = (e) => ESTADOS.find(s => s.key === e)?.color || 'bg-slate-800 text-slate-400';
const estadoLabel = (e) => ESTADOS.find(s => s.key === e)?.label || e;

export default function CarwashOperario() {
  const { usuario, cerrarSesion } = useAuth();
  const [turnos, setTurnos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    try {
      const res = await api.get('/carwash/turnos');
      setTurnos(res.data);
    } catch { toast.error('Error al cargar turnos'); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); const iv = setInterval(cargar, 30000); return () => clearInterval(iv); }, [cargar]);

  const cambiarEstado = async (turnoId, nuevoEstado) => {
    try {
      await api.patch(`/carwash/turnos/${turnoId}/estado`, { estado: nuevoEstado });
      toast.success(`Turno actualizado a: ${estadoLabel(nuevoEstado)}`);
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const siguienteEstado = (actual) => {
    const flujo = ['en_espera', 'lavando', 'listo'];
    const idx = flujo.indexOf(actual);
    return idx >= 0 && idx < flujo.length - 1 ? flujo[idx + 1] : null;
  };

  const turnosActivos = turnos.filter(t => t.estado !== 'entregado' && t.estado !== 'cancelado');
  const turnosTerminados = turnos.filter(t => t.estado === 'entregado');

  return (
    <div className="min-h-screen bg-slate-950 pb-20" data-testid="operario-page">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <Droplets className="w-5 h-5 text-cyan-400" />
          <div>
            <p className="text-sm font-bold text-slate-100">{usuario?.tenant_nombre}</p>
            <p className="text-[10px] text-slate-500">{usuario?.nombre} - Operario</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={cargar} variant="ghost" size="sm" className="text-slate-400 hover:text-slate-200" data-testid="refresh-turnos-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={cerrarSesion} variant="ghost" size="sm" className="text-slate-400 hover:text-red-400">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="px-4 py-4 space-y-3 max-w-lg mx-auto">
        <h2 className="text-xs uppercase tracking-wider text-slate-500 font-bold">
          Turnos del dia ({turnosActivos.length} activos)
        </h2>

        {cargando ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : turnosActivos.length === 0 ? (
          <div className="text-center py-12">
            <Car className="w-10 h-10 text-slate-700 mx-auto mb-2" />
            <p className="text-sm text-slate-600">No hay turnos pendientes</p>
          </div>
        ) : (
          <div className="space-y-3">
            {turnosActivos.map(t => {
              const sig = siguienteEstado(t.estado);
              return (
                <Card key={t.id} className="bg-slate-900/60 border-slate-800" data-testid={`turno-card-${t.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-lg font-bold text-slate-100 font-mono">{t.numero_turno}</p>
                        <p className="text-xs text-slate-500">{t.placa} - {t.tipo_vehiculo}</p>
                      </div>
                      <Badge className={estadoColor(t.estado) + ' text-xs'}>{estadoLabel(t.estado)}</Badge>
                    </div>
                    {t.cliente_nombre && <p className="text-xs text-slate-400 mb-1">{t.cliente_nombre}</p>}
                    <p className="text-sm text-slate-300 mb-3">{t.servicio_nombre || 'Sin servicio'}</p>
                    {t.hora_agendada && (
                      <p className="text-[10px] text-cyan-500 flex items-center gap-1 mb-2">
                        <Clock className="w-3 h-3" /> Agendado: {t.hora_agendada}
                      </p>
                    )}
                    {sig && (
                      <Button onClick={() => cambiarEstado(t.id, sig)}
                        className={`w-full font-bold rounded-sm text-sm ${
                          sig === 'lavando' ? 'bg-sky-500 hover:bg-sky-600 text-white' : 'bg-emerald-500 hover:bg-emerald-600 text-black'
                        }`}
                        data-testid={`cambiar-estado-${t.id}`}>
                        {sig === 'lavando' ? 'Iniciar Lavado' : 'Marcar Terminado'}
                      </Button>
                    )}
                    {t.estado === 'listo' && (
                      <p className="text-[10px] text-emerald-500 text-center mt-2">Esperando cobro para entregar</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {turnosTerminados.length > 0 && (
          <>
            <h2 className="text-xs uppercase tracking-wider text-slate-600 font-bold pt-4">
              Entregados ({turnosTerminados.length})
            </h2>
            <div className="space-y-2">
              {turnosTerminados.slice(0, 5).map(t => (
                <div key={t.id} className="flex items-center justify-between bg-slate-900/30 border border-slate-800/50 rounded-sm px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-slate-500">{t.numero_turno}</span>
                    <span className="text-xs text-slate-600">{t.placa}</span>
                  </div>
                  <Badge className="bg-slate-800 text-slate-500 border-slate-700 text-[10px]">Entregado</Badge>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
